<template>
  <div>
    <list-header :title="`Outlet: ${getBranchNameById(selectedBranch.id)}`" />

    <!-- Display Image & Price -->
    <item-info-container
      :imageURL="service.imageURL"
      :originalPrice="service.originalPrice"
      :salePrice="service.salePrice"
      :hidePrice="init.hideServicePrice"
      :name="service.name"
    />

    <div class="p-4 text-start">
      <div v-if="service.description" class="mb-4 scss-text-white-space-preserved">
        {{ service.description }}
      </div>

      <div class="d-flex flex-row">
        <div class="d-flex flex-column scss-bold w-25">
          <span>Date:</span>
          <span>Time:</span>
          <span>Duration:</span>
          <span>Booked:</span>
          <span>Outlet:</span>
        </div>

        <div class="d-flex flex-column flex-grow-1 text-end text-truncate w-75">
          <span class="text-truncate">
            {{ convertDateFormat(timeslot.selectedDate) }}
          </span>
          <span class="text-truncate">
            {{ durationOfService }}
          </span>
          <span class="text-truncate">
            {{ formatMinutesUpperCase(service.processingTime) }}
          </span>
          <span class="text-truncate">
            {{ timeslot.selectedEmployee.firstName }}
          </span>
          <span class="text-truncate">
            {{ selectedBranch.name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Enter Remark -->
    <list-header title="Remarks" />

    <div class="p-3">
      <textarea
        class="form-control style-textarea"
        id="specialRequest"
        rows="5"
        placeholder="Special Request"
        v-model="remarks"
      ></textarea>
    </div>

    <!-- Terms & Conditions -->
    <terms-and-conditions />

    <!-- Payment Summary -->
    <div v-if="!service?.prepaidItemId">
      <list-header title="Payment" />

      <div
        class="p-4 style-payment-summary d-flex flex-row"
        style="line-height: 30px"
      >
        <div class="d-flex flex-column text-start pe-3">
          <span class="scss-bold">Payment Method:</span>
          <template v-if="!init.hideServicePrice">
            <span class="scss-text-light-color">Subtotal:</span>
            <span class="scss-text-light-color">Tax:</span>
            <span class="scss-bold">Total:</span>
          </template>
        </div>
        <div class="d-flex flex-column text-end flex-grow-1">
          <!-- Select Payment Methods -->
          <div
            class="d-flex flex-row scss-click-effect justify-content-end scss-bold scss-text-blue"
            data-bs-toggle="modal"
            data-bs-target="#selectPaymentMethod"
          >
            <div class="text-end me-1">
              {{ formatPaymentMethodName(selectedPaymentMethod) }}
            </div>
            <div class="d-flex flex-column" style="-webkit-text-stroke: 1px">
              <i class="bi bi-chevron-right align-self-center"></i>
            </div>
          </div>
          <template v-if="!init.hideServicePrice">
            <div>
              {{ `${branchCurrencySymbol} ${formatPrice(saleTicketData?.subtotal)}` }}
            </div>
            <div>
              {{ `${branchCurrencySymbol} ${formatPrice(saleTicketData?.tax)}` }}
            </div>
            <div class="scss-bold">
              {{ `${branchCurrencySymbol} ${formatPrice(saleTicketData?.taxedTotal)}` }}
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Buy Now Button -->
    <default-button
      title="Book Now"
      @click="handleBooking"
      :disabled="selectedPaymentMethodId === null"
    />
    <!-- Modal to select payment method -->
    <div
      class="modal fade"
      id="selectPaymentMethod"
      tabindex="-1"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLabel">
              Select Payment Method
            </h5>
            <button
              type="button"
              class="close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body p-0 py-2">
            <div v-if="paymentMethods.length">
              <radio-button-group
                :options="paymentMethods"
                :formatDisplayNameFunction="formatPaymentMethodName"
                v-model="selectedPaymentMethodId"
                data-bs-dismiss="modal"
              />
            </div>
            <div v-else>
              <item-not-found
                message="No payment methods found."
                style="margin-top: 0px !important"
              />
            </div>
          </div>
          <div class="modal-footer">&nbsp;</div>
        </div>
      </div>
    </div>
    <fiserv-form
      v-if="fiservPayment"
      :fiservUrl="saleTicketData.url ? saleTicketData.url : null"
    />
  </div>
</template>

<script>
import moment from "moment";
import FiservForm from "@/views/Home/MakeAppointment/Fiserv/FiservForm.vue";
import ListHeader from "@/components/ListHeader.vue";
import DefaultButton from "@/components/button/DefaultButton.vue";
import RadioButtonGroup from "@/components/RadioButtonGroup.vue";
import ItemInfoContainer from "@/components/ItemInfoContainer.vue";
import TermsAndConditions from "@/components/TermsAndConditions.vue";
import { ticketStatusList } from "@/common/constant/TicketStatus";
import {
  saleTicketEstimate,
  saleTicket,
  getSaleTicket,
} from "@/services/saleTicket";
import { mapMutations, mapGetters, mapActions } from "vuex";
import { useToast } from "vue-toastification";
import { getToken } from "@/services/jwtService";

import { formatPrice } from "@/common/helpers/PriceHelper.js";
import {
  convertDateFormat,
  formatMinutesUpperCase,
  addMinutesToTime,
  convertDateTimeToUTC,
} from "@/common/helpers/DateTimeHelper.js";
import { getBranchNameById } from "@/common/helpers/BaseHelper";
import { paymentGatewayProvider } from "@/common/constant/PaymentTypes";
import { stripePaymentMethod } from "@/common/constant/Stripe.js";
import ItemNotFound from "@/components/ItemNotFound.vue";

export default {
  name: "Booking Summary",
  components: {
    ListHeader,
    DefaultButton,
    RadioButtonGroup,
    ItemInfoContainer,
    TermsAndConditions,
    FiservForm,
    ItemNotFound,
  },
  data() {
    return {
      selectedPaymentMethodId: null,
      service: null,
      timeslot: null,
      remarks: "",
      saleTicketData: null,
      fiservPayment: false,
      loopPayment: null,
    };
  },
  methods: {
    getBranchNameById,
    resetData(status) {
      sessionStorage.removeItem("timeslot");
      sessionStorage.removeItem("serviceDetails");
      this.showLoading(false);
      clearInterval(this.loopPayment);
      if (status) {
        this.$router.push(`/make-appointment/payment/${status}`);
      }
    },
    async fetchSaleTicket() { // get sale ticket estimate total figure
      this.showLoading(true);

      // Form payload data
      // note: 1. no need set paymentMethod payload if is estimate
      const payload = {
        branch: this.timeslot.branch,
        date: convertDateTimeToUTC({
          dateTime: `${this.timeslot.selectedDate} ${this.timeslot.selectedTime}`,
          timezone: this.selectedBranch.timezone,
        }),
        employeeId: this.timeslot.selectedEmployee.id,
        isEstimate: true,
        isOnlineBooking: true,
        isOnlinePayment: parseInt(this.selectedPaymentMethodId) !== 0,
        paymentMethod: this.selectedPaymentMethodId,
        items: [
          {
            productId: this.service.id,
            promotionId: this.service.promotion ? this.service.promotionId : 0,
            isPrepaidBuyAndUse: false,
          },
        ],
        processingTime: this.service.processingTime,
        remark: "",
      };

      try {
        let response;

        // if user login, use saleTicket API, else use saleTicketEstimate API
        // 1. CWA allow user to navigation until payment page(page that show total, subtotal, tax and taxed total. Before actual booking page)
        //    so need to check whether user are logging
        // 2. saleTicket API required user login, and more accurate
        // 3. saleTicketEstimate API does not required user login, less accurate than saleTicket API
        if (this.isLoggedIn) {
          response = await saleTicket(payload);
        } else {
          response = await saleTicketEstimate(payload);
        }

        this.saleTicketData = response.data;
      } catch (error) {
        console.log(error);
      }

      this.showLoading(false);
    },
    async handleBooking() {
      // If not logged in
      if (!this.isLoggedIn) {
        this.$router.push("/login");
        return;
      }

      const isAvailableBranch = this.branches.some(
        (branch) => branch.id === this.selectedBranch.id
      );

      if (!isAvailableBranch) {
        useToast().error(
          "There is some problem accessing the outlet, kindly contact the salon for assistance."
        );
        return;
      }

      this.showLoading(true);

      const { gateway, type } = this.paymentMethod;
      const isFiserv =
        gateway?.provider === paymentGatewayProvider.FISERV ? true : false;
      const isStripe =
        gateway?.provider === paymentGatewayProvider.STRIPE ? true : false;

      const isprepaidItemId = this.service.prepaidItemId > 0; // prepaidItemId is another db table primary key value, if valid should be > 0

      // Form payload data
      const payload =
          !isprepaidItemId
          ? {
              branch: this.timeslot.branch,
              date: convertDateTimeToUTC({
                dateTime: `${this.timeslot.selectedDate} ${this.timeslot.selectedTime}`,
                timezone: this.selectedBranch.timezone,
              }),
              employeeId: this.timeslot.selectedEmployee.id,
              isEstimate: false,
              isOnlineBooking: true,
              isOnlinePayment: parseInt(this.selectedPaymentMethodId) !== 0,
              items: [
                {
                  productId: this.service.id,
                  promotionId: this.service.promotion ? this.service.promotionId : 0,
                  isPrepaidBuyAndUse: type === "GW",
                },
              ],
              processingTime: this.service.processingTime,
              remark: this.remarks,
              paymentMethod: this.selectedPaymentMethodId,
            }
          : {
              branch: this.timeslot.branch,
              date: convertDateTimeToUTC({
                dateTime: `${this.timeslot.selectedDate} ${this.timeslot.selectedTime}`,
                timezone: this.selectedBranch.timezone,
              }),
              employeeId: this.timeslot.selectedEmployee.id,
              isEstimate: false,
              isOnlineBooking: true,
              items: [
                {
                  prepaiditemId: this.service.prepaidItemId,
                },
              ],
              processingTime: this.service.processingTime,
              remark: this.remarks,
              paymentMethod: this.selectedPaymentMethodId,
            };

      try {
        // Post sale ticket
        const res = await saleTicket(payload);
        this.saleTicketData = res.data;
        const { status } = res.data;

        if (isFiserv && !isprepaidItemId) {
          const x = window;
          x.open("about:blank", "_target");
          const { date, hash, taxedTotal, token, id } = res.data;
          const datetime = moment(date)
            .utc()
            .format("YYYY:MM:DD-HH:mm:ss")
            .toString();

          sessionStorage.setItem(
            "fiservForm",
            JSON.stringify({
              txndatetime: datetime,
              hash: hash,
              chargetotal: taxedTotal,
              token: token,
              storename: gateway?.key1 ? gateway.key1 : "",
              sharedsecret: gateway?.key2 ? gateway.key2 : "",
            })
          );
          this.fiservPayment = true;
          this.showLoading(true);

          let timeCount = 180;
          this.loopPayment = setInterval(async () => {
            try {
              const res = await getSaleTicket(id);
              const data = res.data;
              if (res.data.status === ticketStatusList.VOIDED) {
                this.resetData("failed");
              }
              if (data.tempPayment !== undefined) {
                if (parseInt(data.tempPayment.paymentAmount || "") > 0) {
                  this.resetData("success");
                  this.showLoading(false);
                }
              } else {
                if (parseInt(data.paymentAmount || "") > 0) {
                  this.resetData("success");
                  this.showLoading(false);
                }
              }
              if (timeCount <= 0) {
                this.resetData("failed");
              }

              timeCount -= 5;
            } catch (error) {
              this.resetData("failed");
              this.showLoading(false);
            }
          }, 5000);
        } else if (isStripe && !isprepaidItemId) {
          // Set saleTicket details to sessionstorage
          sessionStorage.setItem("saleTicket", JSON.stringify(res.data));
          sessionStorage.setItem("selectedPaymentMethod", JSON.stringify(this.selectedPaymentMethod));
          this.$router.push("/make-appointment/payment/online-payment");
        } else if (isprepaidItemId) {
          // Prepaid Book
          this.resetData();
          sessionStorage.setItem("booking-status", status);
          this.$router.push("/make-appointment/payment/completed");
        } else {
          // Pay at store
          this.resetData();
          sessionStorage.setItem("booking-status", status);
          this.$router.push("/make-appointment/payment/completed");
        }
      } catch (error) {
        const errorMsgList = {
          "Sale Ticket overlap found." : "This booking slot is already taken. Please choose another time.",
          "Invalid Employee for no preference employee." : "This booking slot is already taken. Please choose another time.",
          "You are not allowed to backdate transactions." : "This selected booking time has passed. Please select another available time slot.",
          "Employee Schedule overlap found." : "This employee is unavailable for the selected time slot. Please select a different employee or time.",
          "Not allow to book as Walk In Customer." : "This booking cannot be processed. Please contact the person in charge for assistance.",
          "default" : "Oops! Something went wrong. Please try again.",
        };
        const errorMsg_ = error?.data?.message;
        const errorMsg = (errorMsg_ in errorMsgList) ? errorMsg_ : "default";

        this.showLoading(false);
        useToast().error(errorMsgList[errorMsg]);
      }
    },
    setSelectedPaymentMethod() {
      // If default selected payment method exists
      if (this.paymentMethod) {
        this.selectedPaymentMethodId = this.paymentMethod.id;
      }
    },
    formatPaymentMethodName(paymentMethod) {
      if (!paymentMethod) {
        return;
      }
      // If payment method is gateway
      if (paymentMethod.type === "GW") {
        // If is Paynow
        if (
          paymentMethod.gateway.provider === paymentGatewayProvider.STRIPE &&
          paymentMethod.gateway.key2 === stripePaymentMethod.PAYNOW
        ) {
          return "PayNow";
        }

        return "Credit / Debit Card";
      }

      // Not a gateway payment
      return paymentMethod.name;
    },
    ...mapActions(["getCustomerBranches"]),
    ...mapMutations(["showLoading"]),
    formatPrice,
    convertDateFormat,
    formatMinutesUpperCase,
  },
  computed: {
    selectedPaymentMethod() {
      return this.paymentMethods.find(
        (method) => method.id === parseInt(this.selectedPaymentMethodId)
      );
    },
    durationOfService() {
      return `${this.timeslot.selectedTime} - ${addMinutesToTime(
        this.timeslot.selectedTime,
        this.service.processingTime
      )}`;
    },
    isLoggedIn() {
      return getToken() != null;
    },
    ...mapGetters([
      "init",
      "paymentMethod",
      "paymentMethods",
      "branchCurrencySymbol",
      "branches",
      "selectedBranch",
      "loginUserInfo",
    ]),
  },
  watch: {
    selectedPaymentMethod(newValue) {
      this.$store.commit("setPaymentMethod", newValue);

      if (this.service) {
        this.fetchSaleTicket();
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    const service = JSON.parse(sessionStorage.getItem("serviceDetails"));
    const timeslot = JSON.parse(sessionStorage.getItem("timeslot"));
    if (!service || !timeslot) {
      next("/error");
    }

    next();
  },
  created() {
    this.setSelectedPaymentMethod();
    if (this.isLoggedIn) {
      this.getCustomerBranches(this.loginUserInfo.id);
    }
    // Get data stored in sessionstorage
    // Get service details
    this.service = JSON.parse(sessionStorage.getItem("serviceDetails"));
    this.timeslot = JSON.parse(sessionStorage.getItem("timeslot"));

    // Load neccessary data
    // - Get sale ticket estimate total figure
    this.fetchSaleTicket();
  },
};
</script>

<style lang="scss" scoped>
.style-term-condition {
  font-size: $small-font-size;
}

.style-textarea {
  resize: none;
  -webkit-border-radius: 10px;
  -moz-border-radius: 10px;
  border-radius: 10px;
}

textarea:focus {
  outline: none;
  box-shadow: none;
  border-color: #4a4a4a;
}

.style-booking-item-container {
  width: 90%;
  margin: 20px auto;
}

button {
  border: none;
  background-color: white;
  font-size: 25px;
}
</style>
